<template>
  <span class="input-workspacefolder">
    <input-dropdown
      v-model:value="selectedWorkspace"
      :options="workspaces"
      prop="name"
      :label="$t('words.workspace', 1)"
      :searchable="true"
      :error="errors?.workspace"
    />
    <div class="input-workspacefolder__seperator"></div>
    <div
      v-if="workspaceFolders.length == 0"
      class="input-workspacefolder__newfolder"
    >
      <template v-if="areWorkspaceFoldersLoaded">
        <button
          v-show="!data.isCreatingFolder"
          class="button"
          type="button"
          @click="data.isCreatingFolder = true"
        >
          {{ $t("labels.newFolder") }}
        </button>

        <input-text
          v-show="data.isCreatingFolder"
          ref="createFolderNameInput"
          v-model:value="data.newFolderName"
          :label="$t('labels.newFolderName')"
          :prevent-submit="true"
          @enter="createFolderNameInput?.blur()"
          @blur="onCreateFolder()"
        />
        <button
          v-show="data.isCreatingFolder"
          class="button-icon"
          type="button"
          @click="data.isCreatingFolder = false"
        >
          <lucide-icon name="x" />
        </button>
      </template>
      <template v-else>
        <span
          v-if="selectedWorkspace"
          class="input-workspacefolder__emptymessage"
        >
          {{ $t("messages.loadingData") }}
        </span>
        <span v-else class="input-workspacefolder__emptymessage">{{
          $t("messages.chooseWorkspace")
        }}</span>
      </template>
    </div>
    <input-dropdown
      v-else
      v-model:value="selectedFolder"
      :options="workspaceFolders"
      prop="name"
      :label="$t('labels.folder')"
      :searchable="true"
      :error="errors?.folder"
    />
  </span>
</template>

<script setup lang="ts">
import { computed, ref, watch, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useWorkspaces } from "@/api/workspaces.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputText from "@core/input/InputText.vue";
import { showError } from "@core/ui/alert/alert";

import type { Workspace, WorkspaceFolder } from "@/api/Workspace";


const props = withDefaults(defineProps<{
  workspaceId?: number;
  folderId?: number;
  errors?: { workspace?: string; folder?: string };
}>(), {
  errors: () => ({})
});

const emit = defineEmits<{
  (e: "update:workspaceId", value: number): void;
  (e: "update:folderId", value: number): void;
  (e: "update:folderSlug", value: string): void;
}>();

const { t } = useI18n();
const { workspaces, createFolder, loadWorkspace, loadWorkspaces } =
  useWorkspaces();

const createFolderNameInput = ref<InstanceType<typeof InputText>>();
const data = reactive({
  isCreatingFolder: false,
  newFolderName: ""
});

const selectedWorkspace = computed<Workspace | null>({
  get() {
    if (props.workspaceId == null) return null;
    return workspaces?.[props.workspaceId];
  },
  set(value: { id: number } | null) {
    if (value == null) return;
    emit("update:workspaceId", value.id);
  }
});

const areWorkspaceFoldersLoaded = computed(() => {
  return selectedWorkspace.value?.folders != null;
});

const workspaceFolders = computed(() => {
  const folders = (selectedWorkspace.value?.folders ?? []).slice();
  folders.sort((a, b) => {
    if (a.order < b.order) return -1;
    else if (a.order == b.order) return 0;
    else return 1;
  });
  return folders;
});

const selectedFolder = computed<WorkspaceFolder | undefined>({
  get() {
    const folders = workspaceFolders.value ?? [];
    return folders.find((f) => f.id == props.folderId);
  },
  set(value: { id?: number; slug?: string } | undefined) {
    if (value == null) return;
    if (value.id != null) emit("update:folderId", value.id);
    if (value.slug != null) emit("update:folderSlug", value.slug);
  }
});

watch(() => workspaceFolders.value, (folders) => {
  if (folders.length == 0) selectedFolder.value = undefined;
  else if (selectedFolder.value == null) {
    selectedFolder.value = folders[0];
  }
}, { immediate: true });

watch(() => props.workspaceId, (workspaceId) => {
  if (workspaceId) {
    loadWorkspace(workspaceId);
  }
}, { immediate: true });

loadWorkspaces();

async function onCreateFolder() {
  if (data.newFolderName == "") {
    data.isCreatingFolder = false;
    return;
  }

  if (selectedWorkspace.value == null) {
    showError(t("errors.workspaceNotFound"));
    return;
  }

  try {
    await createFolder({
      workspace: selectedWorkspace.value.id,
      name: data.newFolderName
    });
    data.newFolderName = "";
    data.isCreatingFolder = false;
    selectedFolder.value = workspaceFolders.value[0];
  } catch (error) {
    showError(error as Error);
  }
}
</script>

<style lang="scss">
.input-workspacefolder {
  display: inline-grid;
  gap: 1.5rem;
  grid-template-columns: 1fr min-content 1fr;

  &__seperator {
    flex-shrink: 0;
    width: 1px;
    padding-top: 1.5rem;
    max-height: 4rem;
    &::after {
      content: "";
      display: block;
      height: 100%;
      background-color: var(--color-text-lighter);
      transform: rotate(15deg);
    }
  }

  &__emptymessage {
    align-self: flex-end;
    padding: 0.75rem 0;
  }

  &__newfolder {
    width: 100%;
    display: flex;

    button {
      align-self: flex-end;
    }
  }
}
</style>
